/**
 * LoggerInfo Component
 *
 * This component serves as the main container for displaying and managing logger information
 * for a specific station. It handles the fetching of logger data and conditionally renders
 * either the logger information display, an "Add New Logger" button, or a loading state.
 *
 * Key Features:
 * - Fetches logger data for a specific station using the station's UID
 * - Implements React Query for efficient data fetching and caching
 * - Provides a user-friendly interface for adding new logger information
 * - Handles loading and error states appropriately
 *
 * Data Flow:
 * 1. Component receives station UID from URL parameters
 * 2. Fetches logger data using the UID
 * 3. Based on data availability, renders either:
 *    - LoggerInfoDisplay (if data exists)
 *    - AddLoggerDialog (if user chooses to add new logger)
 *    - Empty state with "Add New Logger" button
 */

import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  Paper,
} from "@mui/material";
import { useQuery } from "react-query";
import { getFormData } from "../../../services/api";
import LoggerInfoDisplay from "./LoggerInfoDisplay";
import AddLoggerDialog from "./AddLoggerDialog";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

const LoggerInfo = () => {
  const { uid } = useParams();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Fetch logger data using React Query
  const {
    data: loggerData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["loggerInfo", uid],
    () => getFormData(`/metadata/logger-info/${uid}`),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const handleAddLogger = () => {
    setIsAddDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsAddDialogOpen(false);
  };

  const handleLoggerAdded = () => {
    setIsAddDialogOpen(false);
    refetch(); // Refresh the data after adding a new logger
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">Error loading logger information</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>
          Logger Information
        </Typography>

        {/* Display existing logger data */}
        <LoggerInfoDisplay loggerData={loggerData || []} />

        {/* Toggle Button */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="contained"
            color={isAddDialogOpen ? "error" : "primary"}
            startIcon={isAddDialogOpen ? <CloseIcon /> : <AddIcon />}
            onClick={isAddDialogOpen ? handleDialogClose : handleAddLogger}
          >
            {isAddDialogOpen ? "Cancel" : "Add New Logger"}
          </Button>
        </Box>

        {/* Add Logger Dialog */}
        {isAddDialogOpen && <AddLoggerDialog onAdd={handleLoggerAdded} />}
      </Paper>
    </Box>
  );
};

export default LoggerInfo;
